import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  Ban,
  Download,
  Eye,
  Pencil,
  Send,
} from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { CancelImmunisationModal } from "components/Modal/CancelImmunisationModal";
import { DataTable } from "components/ui/DataTable";
import { useDataTableSelection } from "components/ui/DataTable/hooks/useDataTableSelection";
import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { PpsnClaimBlockedDialog } from "features/immunizations/components/PpsnClaimBlockedDialog";
import { useDeleteImmunisation } from "features/immunizations/hooks/useDeleteImmunisation";
import { useSubmitVaccineToHealthlink } from "features/immunizations/hooks/useSubmitVaccineToHealthlink";
import {
  formatAdministeredElsewhere,
  isAdministeredElsewhere,
} from "features/immunizations/utils/administeredElsewhere";
import {
  formatDisregardedReason,
  isDisregardedDose,
} from "features/immunizations/utils/disregardedDose";
import { formatDate, formatHl7DatesInText } from "utils/dateFormatter";
import {
  canSubmitToHealthlink,
  formatImmunizationStatus,
  formatImmunizationStatusSimplified,
  getImmunizationStatusColors,
  isImmunizationActionRequired,
  isImmunizationCancelled,
  isImmunizationPending,
  isImmunizationRejected,
} from "utils/immunizationStatus";

import { fetchImmunizations } from "../../utils/fetchImmunizations";
import { AddVaccineForm } from "../AddVaccineForm/AddVaccineForm";
import { DOSE_NUMBER_OPTIONS } from "../AddVaccineForm/constants";
import { VaccineDetails } from "../VaccineDetails/VaccineDetails";
import s from "./VaccinesTable.module.css";

const FILTER_GROUPS = [
  {
    id: "healthlink",
    label: "Healthlink",
    type: "multi",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "reaction",
    label: "Reaction",
    type: "multi",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "unknown", label: "Unknown" },
    ],
  },
];

const SORT_OPTIONS = [
  {
    field: "dateGiven",
    direction: "desc",
    label: "Date (Newest first)",
    icon: ArrowDownNarrowWide,
  },
  {
    field: "dateGiven",
    direction: "asc",
    label: "Date (Oldest first)",
    icon: ArrowUpNarrowWide,
  },
];

const getReactionPillColors = (reaction) => {
  if (!reaction || reaction === "-") return PILL_COLORS.reactionUnknown;
  const upper = reaction.toUpperCase();
  if (upper === "YES") return PILL_COLORS.reactionYes;
  if (upper === "NO") return PILL_COLORS.reactionNo;
  return PILL_COLORS.reactionUnknown;
};

const formatReactionText = (reaction) => {
  if (!reaction || reaction === "-") return "-";
  const upper = reaction.toUpperCase();
  if (upper === "YES") return "Yes";
  if (upper === "NO") return "No";
  if (upper === "UNKNOWN") return "Unknown";
  return reaction.charAt(0).toUpperCase() + reaction.slice(1).toLowerCase();
};

export const VaccinesTable = ({ patientId, tenantVal, seletedVaccineType }) => {
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("dateGiven");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeFilters, setActiveFilters] = useState({
    healthlink: [],
    reaction: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [detailsVaccine, setDetailsVaccine] = useState(null);
  const [editVaccine, setEditVaccine] = useState(null);
  const [cancelVaccine, setCancelVaccine] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const selection = useDataTableSelection((row) => row.id);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["vaccines", tenantVal, patientId, seletedVaccineType],
    queryFn: () => fetchImmunizations(tenantVal, patientId),
  });

  const { mutate: deleteVaccine, isPending: isDeletingVaccine } =
    useDeleteImmunisation();

  const handleDownloadSingle = useCallback(
    async (item) => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${tenantVal}/patientimmunisation/print`,
          { patientId: parseInt(patientId), immunisationIds: [item.id] },
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `immunisation-report-${patientId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
        toast.error("Failed to download immunisation report");
      }
    },
    [tenantVal, patientId]
  );

  const cancellableVaccineIds = useMemo(() => {
    if (!data) return new Set();

    const doseRankMap = DOSE_NUMBER_OPTIONS.reduce((accumulator, option, index) => {
      accumulator[option.value] = index;
      return accumulator;
    }, {});

    const latestPerVaccineName = new Map();

    data.forEach((item) => {
      const key = item.vaccineName || "";
      const existing = latestPerVaccineName.get(key);

      const itemDoseRank = doseRankMap[item.doseNumberCode] ?? -1;
      const itemDate = item.dateGiven ? new Date(item.dateGiven).getTime() : 0;

      if (!existing) {
        latestPerVaccineName.set(key, { item, doseRank: itemDoseRank, date: itemDate });
        return;
      }

      const isLaterDose = itemDoseRank > existing.doseRank;
      const isSameDoseLaterDate = itemDoseRank === existing.doseRank && itemDate > existing.date;
      const isSameDoseSameDateHigherId =
        itemDoseRank === existing.doseRank &&
        itemDate === existing.date &&
        item.id > existing.item.id;

      if (isLaterDose || isSameDoseLaterDate || isSameDoseSameDateHigherId) {
        latestPerVaccineName.set(key, { item, doseRank: itemDoseRank, date: itemDate });
      }
    });

    return new Set([...latestPerVaccineName.values()].map(({ item }) => item.id));
  }, [data]);

  const filteredAndSorted = useMemo(() => {
    if (!data) return [];
    return data
      .filter((item) => {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          !search ||
          item.vaccineName?.toLowerCase().includes(searchLower) ||
          item.type?.toLowerCase().includes(searchLower);

        let matchesHealthlink = true;
        if (activeFilters.healthlink.length > 0) {
          const hasHealthlink = !!item.vaccineCodeName;
          if (
            activeFilters.healthlink.includes("yes") &&
            !activeFilters.healthlink.includes("no")
          ) {
            matchesHealthlink = hasHealthlink;
          } else if (
            activeFilters.healthlink.includes("no") &&
            !activeFilters.healthlink.includes("yes")
          ) {
            matchesHealthlink = !hasHealthlink;
          }
        }

        let matchesReaction = true;
        if (activeFilters.reaction.length > 0) {
          const reaction = item.reaction?.toUpperCase() || "";
          const reactionValue =
            reaction === "YES" ? "yes" : reaction === "NO" ? "no" : "unknown";
          matchesReaction = activeFilters.reaction.includes(reactionValue);
        }

        return matchesSearch && matchesHealthlink && matchesReaction;
      })
      .sort((a, b) => {
        const dateA = a.dateGiven ? new Date(a.dateGiven) : new Date(0);
        const dateB = b.dateGiven ? new Date(b.dateGiven) : new Date(0);
        return sortDirection === "desc" ? dateB - dateA : dateA - dateB;
      });
  }, [data, search, activeFilters, sortDirection]);

  const columns = useMemo(
    () => [
      {
        id: "vaccineName",
        header: "Name",
        /*
         * A product name here runs to "Comirnaty Omicron KP.2 (12 years and over)
         * (Pfizer-BioNTech)", which grew this column to 420px and pushed Reaction and the
         * row actions off the right-hand edge (KIN-1894). The floor is now a readable
         * width rather than the content's: with the grid sized to its container (see
         * noScroll below) it is only a minimum, so a long name truncates instead of
         * dragging the table wider than its card, and the tooltip carries the rest.
         */
        width: "minmax(200px, 1.66fr)",
        testId: "vaccine-name",
        /*
         * The "given elsewhere" label sits under the product rather than in the Status
         * column, because it is not a status — the record is SAVED either way. What it
         * changes is who is answerable for the dose, and a clinician has to be able to see
         * that while scanning the list: a dose we gave and a dose we were told about are
         * different kinds of fact, and showing them identically invites someone to treat
         * hearsay as our own record (KIN-1883).
         */
        render: (item) => (
          <span className={s.nameCell}>
            <span>{item.vaccineName || "-"}</span>
            {!!isAdministeredElsewhere(item) && (
              <span className={s.givenElsewhere}>
                {formatAdministeredElsewhere(item)}
              </span>
            )}
            {/* Says the dose does not fill its slot, and why (KIN-1923). Sits under the product
                rather than in Status for the same reason "given elsewhere" does: the record is
                SAVED, the report went and the claim went — what changed is whether the course is
                a dose further on, which is not a status. */}
            {!!isDisregardedDose(item) && (
              <span className={s.doesNotCount}>Does not count towards the course</span>
            )}
          </span>
        ),
        getTooltipText: (item) =>
          [
            item.vaccineName || "-",
            formatAdministeredElsewhere(item),
            formatDisregardedReason(item),
          ]
            .filter(Boolean)
            .join(" · "),
      },
      {
        id: "doseNumberCode",
        header: "Dose Number",
        // A little over half of Name's share: its longest value is "Primary Course - Dose 1",
        // which sits comfortably in what this leaves.
        width: "minmax(140px, 1fr)",
        render: (item) =>
          DOSE_NUMBER_OPTIONS.find((option) => option.value === item.doseNumberCode)?.label || "-",
      },
      {
        id: "type",
        header: "Type",
        // 90px clipped almost every value — "Pneumococcal" and "Covid-19 Booster" both read
        // as "Pneumococ…". The width comes out of Name, which has room to spare.
        width: "215px",
        render: (item) => item.type || "-",
        getTooltipText: (item) => item.type || "-",
      },
      {
        id: "status",
        header: "Status",
        width: "120px",
        render: (item) => {
          const colors = getImmunizationStatusColors(item.status);
          const errors = item.statusErrors?.length > 0 ? item.statusErrors : null;
          const tooltipContent = errors ? (
            <div>
              {errors.map((message, index) => (
                <div key={index}>{formatHl7DatesInText(message)}</div>
              ))}
            </div>
          ) : (
            formatImmunizationStatus(item.status)
          );

          return (
            <Tooltip title={tooltipContent} placement="top" enterDelay={200}>
              <span>
                <Pill
                  color={colors.color}
                  bg={colors.bg}
                  border={colors.border}
                >
                  {formatImmunizationStatusSimplified(item.status)}
                </Pill>
              </span>
            </Tooltip>
          );
        },
      },
      {
        id: "healthlink",
        header: "HL",
        width: "70px",
        render: (item) => (
          <Pill
            {...(item.vaccineCodeName
              ? PILL_COLORS.sentYes
              : PILL_COLORS.sentNo)}
          >
            {item.vaccineCodeName ? "Yes" : "No"}
          </Pill>
        ),
      },
      {
        id: "dateGiven",
        header: "Date",
        width: "100px",
        render: (item) => formatDate(item.dateGiven) || "-",
      },
      {
        id: "reaction",
        header: "Reaction",
        width: "80px",
        testId: "vaccine-reaction",
        render: (item) => {
          const colors = getReactionPillColors(item.reaction);
          return (
            <Pill color={colors.color} bg={colors.bg} border={colors.border}>
              {formatReactionText(item.reaction)}
            </Pill>
          );
        },
      },
    ],
    []
  );

  const {
    submitVaccine,
    isSubmitting,
    ppsnBlockedPatientId,
    clearPpsnBlocked,
  } = useSubmitVaccineToHealthlink({ tenantVal, onSubmitted: refetch });

  const rowActions = useMemo(
    () => [
      {
        label: "View status details",
        icon: AlertTriangle,
        primary: true,
        primaryOnly: true,
        visibleWhen: (item) =>
          isImmunizationRejected(item.status) ||
          isImmunizationActionRequired(item.status),
        color: (item) =>
          isImmunizationRejected(item.status) ? "#b70000" : "#d97706",
        bg: (item) =>
          isImmunizationRejected(item.status) ? "#ffe8e8" : "#fef3c7",
        border: (item) =>
          isImmunizationRejected(item.status) ? "#b70000" : "#fbbf24",
        onClick: (item) => setDetailsVaccine(item),
      },
      {
        // A saved vaccination submits itself, so this is not part of the ordinary flow —
        // it is the way back from an automatic submission that did not arrive. Same
        // visibility rule as the all-patient list, so it appears only on a record a
        // resubmission could actually change (KIN-1820, corrected by KIN-1848).
        label: "Submit to Healthlink",
        icon: Send,
        primary: true,
        /*
         * Never on a dose given elsewhere. The backend refuses one with a 409 rather than
         * a silent no-op, so the control would be a button that always fails — and what it
         * would be attempting is reporting someone else's administration under our own
         * provider id. Both receivers report NOT_APPLICABLE, which already keeps it out of
         * the retryable set, so the explicit test is belt and braces: resting on the
         * receiver vocabulary means a change to it silently starts offering this again.
         */
        visibleWhen: (item) =>
          !isAdministeredElsewhere(item) && canSubmitToHealthlink(item),
        onClick: (item) =>
          submitVaccine({
            immunisationId: item.id,
            administrationMethod: item.administrationMethod,
            patientId,
          }),
        disabled: isSubmitting,
      },
      {
        label: "See details",
        icon: Eye,
        onClick: (item) => setDetailsVaccine(item),
      },
      {
        label: "Download immunisation report",
        icon: Download,
        onClick: handleDownloadSingle,
      },
      {
        label: "Edit",
        icon: Pencil,
        testId: "vaccine-edit-button",
        onClick: (item) => setEditVaccine(item),
      },
      {
        label: "Cancel vaccine",
        icon: Ban,
        testId: "vaccine-delete-button",
        disabled: (item) => !cancellableVaccineIds.has(item.id),
        disabledReason: "Only the most recent dose can be cancelled",
        onClick: (item) => setCancelVaccine(item),
      },
    ],
    [
      handleDownloadSingle,
      setDetailsVaccine,
      cancellableVaccineIds,
      submitVaccine,
      isSubmitting,
      patientId,
    ]
  );

  const bulkActions = useMemo(
    () => [
      {
        label: isPrinting ? "Downloading..." : "Download immunisation reports",
        icon: Download,
        onClick: async (selectedItems) => {
          if (!selectedItems?.length) return;
          setIsPrinting(true);
          try {
            const axiosInstance = createAxiosInstance();
            const response = await axiosInstance.post(
              `${tenantVal}/patientimmunisation/print`,
              {
                patientId: parseInt(patientId),
                immunisationIds: selectedItems.map((item) => item.id),
              },
              { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `vaccines-report-${patientId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF downloaded successfully");
          } catch (error) {
            console.error("Print failed:", error);
            toast.error("Failed to download PDF report");
          } finally {
            setIsPrinting(false);
          }
        },
      },
    ],
    [tenantVal, patientId, isPrinting]
  );

  const handleFilterChange = useCallback((groupId, value) => {
    setActiveFilters((prev) => ({ ...prev, [groupId]: value }));
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  const hasFilters =
    search ||
    activeFilters.healthlink.length > 0 ||
    activeFilters.reaction.length > 0;

  return (
    <>
      <DataTable
        columns={columns}
        data={filteredAndSorted}
        getRowId={(row) => row.id}
        getRowTestId={() => "vaccine-row"}
        rowActionsLabel="Immunisation actions"
        /*
         * Without this the grid is laid out at max-content, so the flexible tracks resolve
         * against the longest value in the column instead of against the space available —
         * the table then renders wider than its card and scrolls sideways. Sized to the
         * container, the whole table fits with no horizontal scroll (KIN-1894).
         */
        noScroll
        isLoading={isLoading}
        emptyTitle="No vaccines found"
        emptyText={
          hasFilters
            ? "Try adjusting your search or filters to find what you're looking for."
            : "There are no vaccines to display at this time."
        }
        search={{
          value: search,
          onChange: handleSearchChange,
          placeholder: "Search by vaccine name or type",
        }}
        sort={{ field: sortField, direction: sortDirection }}
        sortOptions={SORT_OPTIONS}
        onSortChange={handleSortChange}
        filters={{ value: activeFilters, onChange: handleFilterChange }}
        filtersConfig={FILTER_GROUPS}
        selectable
        selection={selection}
        rowActions={rowActions}
        /* Deliberately unset: DataTable sizes this from the actions themselves, and a
           hardcoded width does not follow when one is added. It was 32px — right for a
           lone overflow menu, too narrow the moment KIN-1820 added a second primary
           action, which is what pushed the icons over the Reaction column. */
        bulkActions={bulkActions}
        currentPage={currentPage}
        rowsPerPage={20}
        onPageChange={setCurrentPage}
        getRowClassName={(row) => {
          // The same dead comparison KIN-1719 §2 found on the tenant-wide table: `status`
          // was matched against "warning" and "error", which the backend never sends, so
          // no row here has ever been styled. Fixed as part of showing cancellations,
          // which cannot be told apart from a live dose otherwise (KIN-1815).
          if (isImmunizationCancelled(row.status)) return "cancelled";
          /* Visually distinct, but never as an error or a cancellation: the dose was given,
             reported and claimed, and nothing about it needs chasing (KIN-1923). Ranked below
             cancellation because a cancelled dose that also did not count is, first, cancelled. */
          if (isDisregardedDose(row)) return "doesNotCount";
          if (isImmunizationRejected(row.status)) return "error";
          if (isImmunizationPending(row.status)) return "warning";
          if (isImmunizationActionRequired(row.status)) return "warning";

          return null;
        }}
      />

      <Dialog
        open={!!detailsVaccine}
        title="Vaccine details"
        onClose={() => setDetailsVaccine(null)}
        width={1050}
      >
        {detailsVaccine && (
          <VaccineDetails
            onOpenEditClick={() => {
              setEditVaccine(detailsVaccine);
              setDetailsVaccine(null);
            }}
            vaccine={detailsVaccine}
            closeModal={() => setDetailsVaccine(null)}
            tenantVal={tenantVal}
            patientId={patientId}
          />
        )}
      </Dialog>

      <Dialog
        open={!!editVaccine}
        title="Edit vaccine"
        onClose={() => setEditVaccine(null)}
        width={1400}
      >
        {editVaccine && (
          <AddVaccineForm
            currentPatientId={patientId}
            closeModal={() => setEditVaccine(null)}
            tenantVal={tenantVal}
            vaccine={editVaccine}
          />
        )}
      </Dialog>

      <PpsnClaimBlockedDialog
        patientId={ppsnBlockedPatientId}
        onClose={clearPpsnBlocked}
      />

      <CancelImmunisationModal
        isOpen={!!cancelVaccine}
        onClose={() => setCancelVaccine(null)}
        onConfirm={() => {
          if (cancelVaccine) {
            deleteVaccine(cancelVaccine.id, {
              onSuccess: () => setCancelVaccine(null),
            });
          }
        }}
        isLoading={isDeletingVaccine}
      />
    </>
  );
};
